import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Loader2, Shield } from 'lucide-react'
import { API_BASE_URL } from '../lib/api'

// DW-FE-M365-CALLBACK (docs/SECOND_TENANT_REHEARSAL.md BLIND-1).
//
// Microsoft's admin-consent registration uses a FRONTEND redirect_uri:
//   {FRONTEND_URL}/onboarding/microsoft/consent-callback
// (see backend app/services/onboarding.py, onboarding/wizard.py, and
// smb_onboarding.py — all build this same URL). But the real work — verifying
// the signed `state` JWT, updating the MonitoredAccount, and testing the live
// connection — happens on the backend at
//   {API}/api/v1/onboarding/microsoft/consent-callback
// which then 302s the browser to /onboarding/{success,error} (or, for the
// SMB self-serve flow that shares this redirect URI, /smb/consent-{complete,
// failed}). See backend app/routers/onboarding.py::microsoft_consent_callback.
//
// This page is therefore a thin forwarder: it exists only so the SPA's
// catch-all Route no longer swallows Microsoft's redirect and bounces the
// customer to the login screen (losing the consent result entirely). It hands
// the untouched query string (state / admin_consent / tenant / error /
// error_description) straight to the backend and lets the backend land the
// customer on the calm success/error page. It does NOT — and must not —
// interpret the params itself: verifying the state signature and touching the
// account are backend-only responsibilities.

/**
 * Build the backend consent-callback URL, preserving Microsoft's query string
 * verbatim. Kept pure and exported so the forward target is unit-testable
 * without driving a real browser navigation.
 */
export function buildConsentCallbackUrl(search: string): string {
  return `${API_BASE_URL}/api/v1/onboarding/microsoft/consent-callback${search}`
}

export function ConsentCallbackPage() {
  useEffect(() => {
    // Full-page navigation (not fetch): the backend responds with a 302 to a
    // frontend landing page, so the browser must follow it top-level.
    window.location.assign(buildConsentCallbackUrl(window.location.search))
  }, [])

  // Display copy only — the query is still forwarded verbatim above, and the
  // backend remains the sole interpreter of state/tokens. But when Microsoft
  // bounced back with error= (or we somehow landed here with no query at all),
  // telling the customer we're "confirming the permissions you just granted"
  // is a lie: nothing was granted. Pick honest copy without deciding outcomes.
  const search = window.location.search
  const hasQuery = search !== '' && search !== '?'
  const looksLikeSuccessPath =
    hasQuery && !new URLSearchParams(search).has('error')

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-brass" aria-hidden="true" />
        </div>
        {looksLikeSuccessPath ? (
          <>
            <h1 className="text-xl font-serif font-bold text-navy">
              Finalising your Microsoft 365 connection
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              One moment — we’re confirming the return from Microsoft.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-serif font-bold text-navy">
              Returning from Microsoft 365
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              One moment — we’re opening the result of your Microsoft visit. No
              tenant has been connected yet.
            </p>
          </>
        )}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mt-6">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          {looksLikeSuccessPath ? 'Confirming…' : 'Opening…'}
        </div>
      </motion.div>
    </div>
  )
}
